// List page, sort and filters live in the URL (ADR 0005), in react-admin's
// shapes so a portal URL works here and back (ADR 0017):
//   ?filter=<json>&order=ASC|DESC&page=1&perPage=10&sort=field
// Embedded lists keep the same state locally instead (PARITY Q26).
// The last state of each list is remembered, as react-admin's default
// localStorageStore does (RaStore.<list>.listParams): coming back to a list
// with no list params in the URL restores it. Unlike react-admin, only a
// state that returned rows is remembered: one that returned none, or failed,
// is forgotten, so the menu link opens the list in its default scope again.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import type { ListQuery, SortOrder } from './types'

/** This app's key for a list's remembered state (ADR 0017: `oase-admin:*` only). */
export const listMemoryKey = (pathname: string) => `oase-admin:list:${pathname}`

const LIST_KEYS = ['filter', 'order', 'page', 'perPage', 'sort'] as const

const hasListParams = (search: URLSearchParams) => LIST_KEYS.some((key) => search.has(key))

function recall(pathname: string): string | null {
  try {
    return localStorage.getItem(listMemoryKey(pathname))
  } catch {
    return null
  }
}

function forget(pathname: string) {
  try {
    localStorage.removeItem(listMemoryKey(pathname))
  } catch {
    // storage unavailable: nothing was remembered
  }
}

function remember(pathname: string, state: ListState) {
  try {
    localStorage.setItem(
      listMemoryKey(pathname),
      writeListState(new URLSearchParams(), state).toString()
    )
  } catch {
    // storage unavailable: the list still works, it just isn't remembered
  }
}

export type ListFilter = NonNullable<ListQuery['filter']>
export type FilterValue = ListFilter[string]

export interface ListParamsDefaults {
  defaultSort: string
  defaultOrder: SortOrder
  defaultPerPage: number
  /** The portal's `filterDefaultValues`: used while the URL carries no filter. */
  filterDefaults?: ListFilter
}

export interface ListState {
  page: number
  perPage: number
  sort: string
  order: SortOrder
  filter: ListFilter
}

export interface ListParams extends ListState {
  setPage: (page: number) => void
  setPerPage: (perPage: number) => void
  /** Sort by `field`; without `order`, the same field toggles and a new field starts ASC. */
  setSort: (field: string, order?: SortOrder) => void
  setFilter: (filter: ListFilter) => void
  /** Set one filter; null, undefined and '' remove it. */
  setFilterValue: (key: string, value: FilterValue) => void
  /** The request for useList: offsets from page and perPage. */
  query: ListQuery
  /**
   * Report what the current state returned. Rows: it is remembered for the
   * next visit. No rows or an error: the memory is cleared. A no-op for
   * embedded lists.
   */
  settle: (hasRows: boolean) => void
}

const EMPTY_FILTER: ListFilter = {}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined
  const value = Number(raw)
  return value >= 1 && Number.isSafeInteger(value) ? value : undefined
}

function parseFilter(raw: string | null): ListFilter | undefined {
  if (raw === null) return undefined
  try {
    const value: unknown = JSON.parse(raw)
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as ListFilter)
      : undefined
  } catch {
    return undefined
  }
}

/** URL → state; anything invalid falls back to its default. */
export function readListState(search: URLSearchParams, defaults: ListParamsDefaults): ListState {
  const order = search.get('order')
  return {
    page: positiveInt(search.get('page')) ?? 1,
    perPage: positiveInt(search.get('perPage')) ?? defaults.defaultPerPage,
    sort: search.get('sort') || defaults.defaultSort,
    order: order === 'ASC' || order === 'DESC' ? order : defaults.defaultOrder,
    filter: parseFilter(search.get('filter')) ?? defaults.filterDefaults ?? EMPTY_FILTER
  }
}

/** State → URL, keeping unrelated params. Keys in react-admin's (query-string sorted) order. */
export function writeListState(search: URLSearchParams, state: ListState): URLSearchParams {
  const next = new URLSearchParams(search)
  for (const key of ['filter', 'order', 'page', 'perPage', 'sort']) next.delete(key)
  next.set('filter', JSON.stringify(state.filter))
  next.set('order', state.order)
  next.set('page', String(state.page))
  next.set('perPage', String(state.perPage))
  next.set('sort', state.sort)
  return next
}

function withFilterValue(filter: ListFilter, key: string, value: FilterValue): ListFilter {
  const rest = Object.fromEntries(Object.entries(filter).filter(([name]) => name !== key))
  return value === null || value === undefined || value === '' ? rest : { ...rest, [key]: value }
}

function sortChange(state: ListState, field: string, order: SortOrder | undefined): ListState {
  const nextOrder =
    order ?? (field === state.sort ? (state.order === 'ASC' ? 'DESC' : 'ASC') : 'ASC')
  return { ...state, sort: field, order: nextOrder, page: 1 }
}

function toQuery(state: ListState): ListQuery {
  return {
    sort: state.sort,
    order: state.order,
    filter: state.filter,
    start: (state.page - 1) * state.perPage,
    end: state.page * state.perPage
  }
}

type Update = (state: ListState) => ListState

function useListApi(
  state: ListState,
  apply: (update: Update, replace: boolean) => void,
  settle: (hasRows: boolean) => void
): ListParams {
  const setPage = useCallback(
    (page: number) => {
      apply((s) => ({ ...s, page: Math.max(1, Math.trunc(page)) }), false)
    },
    [apply]
  )
  const setPerPage = useCallback(
    (perPage: number) => {
      apply((s) => ({ ...s, perPage: Math.max(1, Math.trunc(perPage)), page: 1 }), false)
    },
    [apply]
  )
  const setSort = useCallback(
    (field: string, order?: SortOrder) => {
      apply((s) => sortChange(s, field, order), false)
    },
    [apply]
  )
  const setFilter = useCallback(
    (filter: ListFilter) => {
      apply((s) => ({ ...s, filter, page: 1 }), true)
    },
    [apply]
  )
  const setFilterValue = useCallback(
    (key: string, value: FilterValue) => {
      apply((s) => ({ ...s, filter: withFilterValue(s.filter, key, value), page: 1 }), true)
    },
    [apply]
  )
  const query = useMemo(() => toQuery(state), [state])
  return { ...state, setPage, setPerPage, setSort, setFilter, setFilterValue, query, settle }
}

/** Defaults with a stable identity while their content is unchanged (inline literals are fine). */
function useStableDefaults({
  defaultSort,
  defaultOrder,
  defaultPerPage,
  filterDefaults
}: ListParamsDefaults) {
  const filterKey = filterDefaults === undefined ? undefined : JSON.stringify(filterDefaults)
  return useMemo<ListParamsDefaults>(
    () => ({
      defaultSort,
      defaultOrder,
      defaultPerPage,
      ...(filterKey !== undefined && { filterDefaults: JSON.parse(filterKey) as ListFilter })
    }),
    [defaultSort, defaultOrder, defaultPerPage, filterKey]
  )
}

/** URL-synced list state. Page, perPage and sort changes push a history entry; filter changes replace. */
export function useListParams(defaults: ListParamsDefaults): ListParams {
  const [search, setSearch] = useSearchParams()
  const { pathname } = useLocation()
  const stable = useStableDefaults(defaults)
  const raw = search.toString()
  // No list params in the URL (the menu, a redirect after save): the
  // remembered state, read synchronously so the first request already uses it.
  const remembered = hasListParams(search) ? null : recall(pathname)
  const source = remembered ?? raw
  const state = useMemo(() => readListState(new URLSearchParams(source), stable), [source, stable])
  // react-router's setter changes identity with the location; keep ours stable
  // so consumers can put setters in effect dependencies.
  const setSearchRef = useRef(setSearch)
  useEffect(() => {
    setSearchRef.current = setSearch
  }, [setSearch])
  // Put the restored state in the URL (replace, so Back skips the bare URL).
  useEffect(() => {
    if (remembered === null) return
    setSearchRef.current(
      (current) => writeListState(current, readListState(new URLSearchParams(remembered), stable)),
      { replace: true }
    )
  }, [remembered, stable])
  // Only a state the URL carries is remembered: a bare URL is the default
  // scope already, and remembering it would make the restore above replace
  // the URL again (racing a navigation away from the list).
  const isInUrl = remembered === null && hasListParams(new URLSearchParams(raw))
  const settle = useCallback(
    (hasRows: boolean) => {
      if (!hasRows) forget(pathname)
      else if (isInUrl) remember(pathname, state)
    },
    [pathname, state, isInUrl]
  )
  const apply = useCallback(
    (update: Update, replace: boolean) => {
      setSearchRef.current(
        (current) => writeListState(current, update(readListState(current, stable))),
        {
          replace
        }
      )
    },
    [stable]
  )
  return useListApi(state, apply, settle)
}

const ignoreOutcome = () => undefined

/** The same API on component state, for embedded lists (PARITY Q26). */
export function useLocalListParams(defaults: ListParamsDefaults): ListParams {
  const [state, setState] = useState<ListState>(() =>
    readListState(new URLSearchParams(), defaults)
  )
  const apply = useCallback((update: Update) => {
    setState(update)
  }, [])
  return useListApi(state, apply, ignoreOutcome)
}
